import React, { useEffect, useRef, useState } from 'react';
import '../styles/Home.css';
import { Link } from 'react-router-dom';
import { Users, Workflow, Cpu } from 'lucide-react';

function Home() {
  const [isVisible, setIsVisible] = useState(false);
  const heroRef = useRef(null);
  
  // Dynamically import all partner logos
  const partnerLogos = require.context('../assets/partners', false, /\.(png|jpe?g|svg)$/);
  const partnerImages = partnerLogos.keys().map(key => ({
    src: partnerLogos(key),
    name: key.replace('./', '').replace(/\.(png|jpe?g|svg)$/, '')
  }));

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const heroElement = heroRef.current;
    if (heroElement) {
      observer.observe(heroElement);
    }

    return () => {
      if (heroElement) {
        observer.unobserve(heroElement);
      }
    };
  }, []);

  return (
    <div className="home">
      <section className="hero-section" ref={heroRef}>
        <div className={`hero-content-split ${isVisible ? 'visible' : ''}`}>
          <div className="hero-left">
            <h1 className="hero-impact-line-1">EVERY SYSTEM ALIGNED</h1>
            <h1 className="hero-impact-line-2">EVERY GOAL ACHIEVED</h1>
          </div>
          <div className="hero-right">
            <h2 className="hero-company">One Aligned, Inc.</h2>
            <p className="hero-tagline">
              Driving efficiency, innovation, and sustainable success<br />through people, processes, and technology
            </p>
            <Link to="/contact" className="started-link">
              <button className="cta-button">Speak with an Expert</button>
            </Link>
          </div>
        </div>
      </section>

      <section className="combined-section">
        <div className="combined-container">
          <div className="pillars-section">
            <div className="pillar">
              <div className="flip-card">
                <div className="flip-card-inner">
                  <div className="flip-card-front">
                    <Users size={60} color="#4dd0e1" strokeWidth={1.5} />
                    <h3 className="pillar-text">People</h3>
                  </div>
                  <div className="flip-card-back">
                    <h4 className="flip-card-subtitle">Leadership, Culture, and Capability</h4>
                    <p className="flip-card-description">
                      Aligning teams around shared outcomes and clear accountability.
                    </p>
                    <p className="flip-card-description">
                      Building the skills, mindset, and behaviors required for quality deliveries.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="pillar">
              <div className="flip-card">
                <div className="flip-card-inner">
                  <div className="flip-card-front">
                    <Workflow size={60} color="#4dd0e1" strokeWidth={1.5} />
                    <h3 className="pillar-text">Processes</h3>
                  </div>
                  <div className="flip-card-back">
                    <h4 className="flip-card-subtitle">How Work Actually Flows</h4>
                    <p className="flip-card-description">
                      Connecting strategy, planning, and execution.
                    </p>
                    <p className="flip-card-description">
                      Reducing friction, duplication, and bottlenecks across teams.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="pillar">
              <div className="flip-card">
                <div className="flip-card-inner">
                  <div className="flip-card-front">
                    <Cpu size={60} color="#4dd0e1" strokeWidth={1.5} />
                    <h3 className="pillar-text">Technology</h3>
                  </div>
                  <div className="flip-card-back">
                    <h4 className="flip-card-subtitle">Equipping Leaders with Tools to Drive Change</h4>
                    <p className="flip-card-description">
                      Using data and digital tools to improve visibility and decision-making.
                    </p>
                    <p className="flip-card-description">
                      Supporting people and processes, not replacing them.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="how-we-serve-section">
            <h2 className="how-we-serve-title">Who We Are</h2>
            <p className="how-we-serve-text">
              One Aligned, Inc. is a <strong>woman-owned, veteran-owned, certified small business</strong> providing strategic-to-operational execution support, enterprise problem solving, expert-level Navy and DoD sustainment services, and ecosystem enablement. Our team consists of <strong>career-long federal leaders</strong> who bring lived experience turning strategy into execution inside complex, mission-critical environments.
            </p>
            <Link to="/services" className="capabilities-link">
              <button className="cta-button">Explore Our Capabilities</button>
            </Link>
          </div>
        </div>
      </section>

      <section className="partners-section">
        <h2 className="section-text">Our Partners</h2>
        <div className="partners-grid">
          {partnerImages.map((partner, index) => (
            <div key={index} className="partner-card">
              <div className="partner-logo-frame">
                <img src={partner.src} alt={partner.name} className="partner-logo-img" />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default Home;
